import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  Suspense,
  useState,
} from 'react';

// ─── Types ───────────────────────────────────────────────────────────

type DialogComponent =
  | React.ComponentType<{ onClose: () => void }>
  | React.LazyExoticComponent<React.ComponentType<{ onClose: () => void }>>;

interface DialogContextValue {
  /** Open a dialog by key. If a component isn't registered, this is a no-op. */
  open: (key: string, props?: Record<string, unknown>) => void;
  /** Close the currently visible dialog. */
  close: () => void;
  /** Register a dialog component under a key. Call once (e.g. at module scope or in a layout). */
  register: (key: string, component: DialogComponent) => void;
}

const DialogContext = createContext<DialogContextValue>({
  open: () => {},
  close: () => {},
  register: () => {},
});

export const useDialog = () => useContext(DialogContext);

// ─── Provider ────────────────────────────────────────────────────────

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [registry] = useState(() => new Map<string, DialogComponent>());
  const [active, setActive] = useState<{
    key: string;
    props?: Record<string, unknown>;
  } | null>(null);

  const register = useCallback(
    (key: string, component: DialogComponent) => {
      registry.set(key, component);
    },
    [registry],
  );

  const open = useCallback(
    (key: string, props?: Record<string, unknown>) => {
      if (registry.has(key)) {
        setActive({ key, props });
      }
    },
    [registry],
  );

  const close = useCallback(() => setActive(null), []);

  const value = useMemo(
    () => ({ open, close, register }),
    [open, close, register],
  );

  const ActiveComponent = active ? registry.get(active.key) : null;

  return (
    <DialogContext.Provider value={value}>
      {children}
      {ActiveComponent && active ? (
        <Suspense
          fallback={
            <div
              style={{
                position: 'fixed',
                inset: 0,
                // Matches the raised upgrade-modal layer (WebDialog zIndex 10000)
                // so the lazy-load scrim never flashes behind a create dialog.
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(15, 23, 42, 0.12)',
              }}
            >
              <div
                className="animate-spin"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9999,
                  border: '3px solid rgba(255,255,255,0.8)',
                  borderTopColor: 'transparent',
                }}
              />
            </div>
          }
        >
          <ActiveComponent onClose={close} {...(active.props || {})} />
        </Suspense>
      ) : null}
    </DialogContext.Provider>
  );
}
